import React, { createContext, useContext, useMemo, useRef, useState } from 'react';
import { safeStorage } from '@/lib/utils/storage';
import { useSessionManager } from '../hooks/useSessionManager';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  // Default to non-blocking on public pages. Protected routes will explicitly
  // call ensureAuthInitialized() and wait for loading to resolve.
  const [loading, setLoading] = useState(false);

  const {
    initializeSession,
    clearSession,
    checkSessionValidity,
    updateLastActivity,
  } = useSessionManager();

  const firebaseRef = useRef({
    initPromise: null,
    auth: null,
    onAuthStateChanged: null,
    signOut: null,
  });
  const authListenerRef = useRef({
    started: false,
    unsubscribe: null,
  });
  const isMountedRef = useRef(true);

  React.useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      try {
        authListenerRef.current.unsubscribe?.();
      } catch (_) {
        // ignore
      }
    };
  }, []);

  const bootstrapFirebaseAuth = async () => {
    if (firebaseRef.current.initPromise) return firebaseRef.current.initPromise;
    firebaseRef.current.initPromise = (async () => {
      const [{ onAuthStateChanged, signOut }, { auth }] = await Promise.all([
        import('firebase/auth'),
        import('@services/firebase'),
      ]);
      firebaseRef.current.onAuthStateChanged = onAuthStateChanged;
      firebaseRef.current.signOut = signOut;
      firebaseRef.current.auth = auth;
      return firebaseRef.current;
    })();
    return firebaseRef.current.initPromise;
  };

  const ensureAuthInitialized = async () => {
    await bootstrapFirebaseAuth();
    if (authListenerRef.current.started) return;

    authListenerRef.current.started = true;
    setLoading(true);

    const { auth, onAuthStateChanged, signOut } = firebaseRef.current;

    authListenerRef.current.unsubscribe = onAuthStateChanged(auth, async firebaseUser => {
      const finish = () => {
        if (isMountedRef.current) setLoading(false);
      };

      if (!firebaseUser) {
        setUser(null);
        clearSession();
        finish();
        return;
      }

      const hasSession = safeStorage.getItem('sessionStart');
      const isNewUser = !hasSession;

      if (!isNewUser && !checkSessionValidity()) {
        signOut(auth)
          .catch(() => {})
          .finally(() => {
            clearSession();
            setUser(null);
            finish();
          });
        return;
      }

      if (isNewUser) initializeSession();

      // Set a lightweight user immediately to allow the UI to render without waiting for network.
      const baseUser = {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        emailVerified: firebaseUser.emailVerified,
        displayName: firebaseUser.displayName,
        photoURL: firebaseUser.photoURL,
        role: 'user',
        permissions: [],
        crd_number: null,
      };
      setUser(prev => prev || baseUser);
      finish();

      const hydrateUser = async () => {
        const userService = await import('../services/userService');

        let claims = {};
        try {
          const tokenResult = await firebaseUser.getIdTokenResult();
          claims = tokenResult?.claims || {};
        } catch (e) {
          console.warn('Failed to load custom claims:', e);
        }

        let userProfile = null;
        try {
          userProfile = await userService.getUserProfile(firebaseUser.uid);
        } catch (e) {
          console.warn('Failed to load user profile:', e);
        }

        if (!userProfile) {
          const newProfile = {
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            firstName: firebaseUser.displayName?.split(' ')[0] || '',
            lastName: firebaseUser.displayName?.split(' ')[1] || '',
            // Mirror Firebase Auth flag into Firestore so downstream
            // components can rely on a single source of truth.
            emailVerified: firebaseUser.emailVerified === true,
            role: 'user',
            createdAt: new Date().toISOString(),
          };

          const profileCreated = await userService.createUserProfile(newProfile);
          userProfile = profileCreated
            ? newProfile
            : { ...newProfile, permissions: [], crd_number: null };
        } else if (firebaseUser.emailVerified && userProfile.emailVerified !== true) {
          // Backfill emailVerified for legacy profiles created before we
          // stored this field. This runs for any provider (including Google)
          // and keeps Firestore in sync with Firebase Auth.
          try {
            await userService.updateUserProfile(firebaseUser.uid, { emailVerified: true });
            userProfile = { ...userProfile, emailVerified: true };
          } catch (e) {
            console.warn('Failed to sync emailVerified to user profile:', e);
          }
        }

        const claimAdminFlag =
          claims.admin === true || claims.isAdmin === true || claims.role === 'admin';
        const normalizedRole =
          claims.role || (claimAdminFlag ? 'admin' : (userProfile?.role || 'user'));

        setUser(prev => ({
          ...(prev || baseUser),
          ...userProfile,
          ...claims,
          admin: claimAdminFlag || claims.admin === true ? true : prev?.admin,
          isAdmin: claimAdminFlag || claims.isAdmin === true ? true : prev?.isAdmin,
          role: normalizedRole,
          permissions: userProfile?.permissions || [],
          crd_number: userProfile?.crd_number || null,
        }));

        if (safeStorage.getItem('sessionStart')) {
          updateLastActivity();
        } else {
          initializeSession();
        }

      };

      hydrateUser().catch(e => console.error('hydrateUser failed', e));
    });
  };

  // Login wrapper to handle session
  const login = async (email, password) => {
    try {
      await ensureAuthInitialized();
      const authService = await import('../services/authService');
      const loggedInUser = await authService.login(email, password);
      initializeSession();
      return loggedInUser;
    } catch (error) {
      throw error;
    }
  };

  // Logout wrapper to handle session
  const logout = async () => {
    try {
      await ensureAuthInitialized();
      const authService = await import('../services/authService');
      await authService.logout();
      clearSession();
    } catch (error) {
      throw error;
    }
  };

  // Refresh the current auth user to get latest flags (like emailVerified)
  const reloadAuthUser = async () => {
    await ensureAuthInitialized();
    const { auth } = firebaseRef.current;
    if (!auth?.currentUser) return false;
    try {
      await auth.currentUser.reload();
      const updated = auth.currentUser;
      setUser(prev => (prev ? { ...prev, emailVerified: updated.emailVerified } : prev));
      return true;
    } catch (e) {
      console.warn('Failed to reload auth user:', e);
      return false;
    }
  };

  // Refresh user profile from Firestore
  const refreshUserProfile = async () => {
    if (!user?.uid) return;
    try {
      await ensureAuthInitialized();
      const userService = await import('../services/userService');
      const profile = await userService.getUserProfile(user.uid);
      if (profile) {
        setUser(prev => ({ ...prev, ...profile }));
      }
    } catch (e) {
      console.error('Failed to refresh user profile:', e);
    }
  };

  // Helper functions for role and permission checking
  const isAdmin = () => {
    // Claims-first admin (do not trust profile role)
    return user?.admin === true || user?.isAdmin === true;
  };

  const isAdvisor = () => {
    return user?.role === 'advisor';
  };

  const hasRole = role => {
    return user?.role === role;
  };

  const hasPermission = permission => {
    return user?.permissions?.includes(permission) || isAdmin();
  };

  const isClaimedAdvisor = advisorId => {
    const tokenAdvisorId = String(user?.advisorId || user?.claimedAdvisorId || '');
    return tokenAdvisorId && String(advisorId) === tokenAdvisorId;
  };

  const canAccessCRD = crd_number => {
    if (isAdmin()) return true;
    if (crd_number == null) return false;
    return isClaimedAdvisor(String(crd_number));
  };

  // Proactive token refresh (every ~55 minutes) and on visibility change
  const getIdToken = async (forceRefresh = false) => {
    await ensureAuthInitialized();
    const { auth } = firebaseRef.current;
    if (!auth?.currentUser) return null;
    return auth.currentUser.getIdToken(forceRefresh);
  };

  const refreshTokenNow = async () => {
    await ensureAuthInitialized();
    const { auth } = firebaseRef.current;
    if (!auth?.currentUser) return false;
    try {
      await auth.currentUser.getIdToken(true);
      const tokenResult = await auth.currentUser.getIdTokenResult(true);
      const claims = tokenResult?.claims || {};
      const claimAdminFlag =
        claims.admin === true || claims.isAdmin === true || claims.role === 'admin';
      setUser(prev =>
        prev
          ? {
              ...prev,
              ...claims,
              admin: claimAdminFlag || claims.admin === true ? true : prev.admin,
              isAdmin: claimAdminFlag || claims.isAdmin === true ? true : prev.isAdmin,
            }
          : prev
      );
      return true;
    } catch (e) {
      return false;
    }
  };

  // NOTE: We intentionally do not start the proactive token refresh timer until
  // auth is initialized, to keep the landing route lightweight.

  const value = useMemo(() => ({
    user,
    loading,
    ensureAuthInitialized,
    register: async (...args) => {
      await ensureAuthInitialized();
      const authService = await import('../services/authService');
      return authService.register(...args);
    },
    login,
    logout,
    createUserProfile: async (...args) => {
      await ensureAuthInitialized();
      const userService = await import('../services/userService');
      return userService.createUserProfile(...args);
    },
    getUserProfile: async (...args) => {
      await ensureAuthInitialized();
      const userService = await import('../services/userService');
      return userService.getUserProfile(...args);
    },
    updateUserProfile: async (...args) => {
      await ensureAuthInitialized();
      const userService = await import('../services/userService');
      return userService.updateUserProfile(...args);
    },
    changeEmail: async (...args) => {
      await ensureAuthInitialized();
      const authService = await import('../services/authService');
      return authService.changeEmail(...args);
    },
    changePassword: async (...args) => {
      await ensureAuthInitialized();
      const authService = await import('../services/authService');
      return authService.changePassword(...args);
    },
    sendPasswordReset: async (...args) => {
      await ensureAuthInitialized();
      const authService = await import('../services/authService');
      return authService.sendPasswordReset(...args);
    },
    resendVerificationEmail: async (...args) => {
      await ensureAuthInitialized();
      const authService = await import('../services/authService');
      return authService.resendVerificationEmail(...args);
    },
    reloadAuthUser,
    refreshUserProfile,
    checkEmailExists: async (...args) => {
      await ensureAuthInitialized();
      const authService = await import('../services/authService');
      return authService.checkEmailExists(...args);
    },
    isAdmin,
    isClaimedAdvisor,
    isAdvisor,
    hasRole,
    hasPermission,
    canAccessCRD,
    refreshTokenNow,
    getIdToken,
    currentUser: user, // Alias for compatibility
  }), [user, loading]);

  return (
    <AuthContext.Provider
      value={value}
    >
      {children}
    </AuthContext.Provider>
  );
}
